import React from 'react';

import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';

import { BodyM, BodyMBold, BodyS, BodySBold, DescriptionS } from 'components/Typography/Typography';

import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/paymentMethodsStyle';

import { dynamicTranslate } from 'components/Translation';
import { PaymentStatusType as IPaymentStatus } from '../../type';

import ErrorIcon from 'assets/img/ic_msg_warning.svg';
import SuccessIcon from 'assets/img/ic_msg_success.svg';

import { getCommonLabels, getPaymentMethodLabels } from 'redux/modules/dynamicLabels/selectors';
import { billValueSelector } from 'redux/modules/bills/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);

const PaymentInitiated = ({ RefNumber }: { RefNumber: string }) => {
	const classes = useStyles();
	const paymentStatusLabels = useSelector(getPaymentMethodLabels)?.paymentResult;

	return (
		<>
			<img src={SuccessIcon} className={classes.bottomSpace} />
			<BodySBold>{paymentStatusLabels?.payment_initiated}</BodySBold>
			<BodyMBold>{paymentStatusLabels?.thanks_you_payment}</BodyMBold>
			<DescriptionS customClassName={classes.marginBottomNone}>
				{dynamicTranslate(paymentStatusLabels?.payment_ref_no_is, RefNumber)}
			</DescriptionS>
			<DescriptionS>{paymentStatusLabels?.payment_status_update}</DescriptionS>
			<Link to="/dashboard/payments/payments-requests" className={classes.cardChangeActionText}>
				{paymentStatusLabels?.view_payment_request_history}
			</Link>
		</>
	);
};

const PaymentSuccess = () => {
	const classes = useStyles();
	const billsValue = useSelector(billValueSelector);
	const commonLabels = useSelector(getCommonLabels);
	const paymentStatusLabels = useSelector(getPaymentMethodLabels)?.paymentResult;

	return (
		<>
			<img src={SuccessIcon} className={classes.bottomSpace} />
			<BodySBold>{paymentStatusLabels?.payment_confirmed}</BodySBold>
			<BodyMBold>{paymentStatusLabels?.thanks_you_payment}</BodyMBold>
			<BodyM>{dynamicTranslate(commonLabels?.price, billsValue)}</BodyM>
			<BodyS>{paymentStatusLabels?.bill_payment_confirm}</BodyS>
		</>
	);
};

const PaymentFailed = ({ RefNumber }: { RefNumber?: string }) => {
	const classes = useStyles();
	const paymentStatusLabels = useSelector(getPaymentMethodLabels)?.paymentResult;

	if (!RefNumber) {
		return <PaymentRejected />;
	}

	const sanitizeLabel = DOMPurify.sanitize(
		dynamicTranslate(paymentStatusLabels?.payment_failed_tracking, RefNumber, RefNumber)
	);

	return (
		<>
			<img src={ErrorIcon} className={classes.bottomSpace} />
			<BodySBold>{paymentStatusLabels?.unable_to_process_request}</BodySBold>
			<DescriptionS>
				<span dangerouslySetInnerHTML={{ __html: sanitizeLabel }} />
			</DescriptionS>
			<Link to="/dashboard/payments/payments-requests" className={classes.cardChangeActionText}>
				{paymentStatusLabels?.view_payment_request_history}
			</Link>
		</>
	);
};

const PaymentError = () => {
	const classes = useStyles();
	const paymentStatusLabels = useSelector(getPaymentMethodLabels)?.paymentResult;

	return (
		<>
			<img src={ErrorIcon} className={classes.bottomSpace} />
			<BodyMBold disableMargin>{paymentStatusLabels?.unable_to_process_request}</BodyMBold>
		</>
	);
};

const PaymentRejected = () => {
	const classes = useStyles();
	const paymentStatusLabels = useSelector(getPaymentMethodLabels)?.paymentResult;

	return (
		<>
			<img src={ErrorIcon} className={classes.bottomSpace} />
			<BodyMBold disableMargin>{paymentStatusLabels?.unable_to_process_request}</BodyMBold>
			<BodyMBold>{paymentStatusLabels?.try_again_later}</BodyMBold>
			<DescriptionS>{paymentStatusLabels?.payment_failed}</DescriptionS>
		</>
	);
};

const PaymentComponentByStatus = (status: IPaymentStatus, RefNumber?: string): React.ReactNode => {
	switch (status) {
		case IPaymentStatus.REJECTED:
		case IPaymentStatus.CREDIT_EXCEEDS_MAX:
		case IPaymentStatus.DEBIT_EXCEEDS_MAX:
			return <PaymentRejected />;

		case IPaymentStatus.FAILED:
			return <PaymentFailed RefNumber={RefNumber} />;

		case IPaymentStatus.SUCCESS:
			return <PaymentSuccess />;

		case IPaymentStatus.INITIATED:
			return <PaymentInitiated RefNumber={RefNumber ?? 'N/A'} />;

		default:
			return <PaymentError />;
	}
};

const PaymentStatus = ({ status, RefNumber }: { status: IPaymentStatus; RefNumber?: string }) => {
	const classes = useStyles();
	const Component = PaymentComponentByStatus(status, RefNumber);

	return (
		<Grid
			container
			justify="center"
			alignItems="center"
			style={{ maxHeight: '350px', minHeight: '250px', height: '100%' }}
		>
			<Grid item lg={6} md={8} sm={12} xs={12}>
				<Card className={classes.boxShadowNone}>
					<CardContent className={classes.securePaymentContainer}>{Component}</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
};

export default PaymentStatus;
